import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import SpeakButton from "@/components/SpeakButton";

const INTEREST_MEDIA = {
  Dinosaurs: "https://images.pexels.com/photos/33722866/pexels-photo-33722866.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  Space: "https://images.pexels.com/photos/7476134/pexels-photo-7476134.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const FALLBACK_INTERESTS = ["Dinosaurs", "Space", "Sports", "Animals", "Art", "Music"];

export default function Learn() {
  const { id } = useParams();
  const nav = useNavigate();
  const [student, setStudent] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pastLessons, setPastLessons] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get(`/students/${id}`),
        api.get(`/lessons/student/${id}`),
      ]);
      setStudent(s.data);
      setPastLessons(l.data);
    } catch {
      nav("/dashboard");
    }
  }, [id, nav]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async (interest) => {
    setBusy(true);
    setLesson(null);
    try {
      const { data } = await api.post("/lessons/generate", { student_id: id, interest });
      setLesson(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create a lesson.");
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!lesson) return;
    try {
      await api.post(`/lessons/${lesson.id}/complete`);
      toast.success("Lesson finished. Great work.");
      setLesson({ ...lesson, completed: true });
      const { data } = await api.get(`/lessons/student/${id}`);
      setPastLessons(data);
    } catch {
      toast.error("Could not save progress.");
    }
  };

  if (!student) return null;

  const interests = (student.interests && student.interests.length > 0)
    ? student.interests : FALLBACK_INTERESTS;

  return (
    <main className="min-h-screen" style={{ background: "var(--tm-bg-2)" }} data-testid="learn-page">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6">
        <Link to={`/students/${id}/home`} className="tm-btn tm-btn-ghost mb-4" data-testid="learn-back">
          <ArrowLeft size={18} /> Back
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl" style={{ background: "var(--tm-bg-3)", color: "var(--tm-brand)" }}>
            <Sparkles size={22} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito, sans-serif" }}>
            Pick what to learn about
          </h1>
        </div>

        {!lesson && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8" data-testid="learn-interests-grid">
            {interests.map((i) => {
              const img = INTEREST_MEDIA[i];
              return (
                <button
                  key={i}
                  onClick={() => generate(i)}
                  disabled={busy}
                  className="tm-card p-0 overflow-hidden text-left hover:border-[color:var(--tm-brand)] transition-colors"
                  data-testid={`learn-interest-${i.toLowerCase()}`}
                >
                  <div
                    className="w-full h-28 sm:h-32"
                    style={{
                      background: img ? `url(${img}) center/cover` : "var(--tm-bg-3)",
                    }}
                    aria-hidden
                  />
                  <div className="p-4">
                    <p className="text-lg font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>{i}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {busy && !lesson && (
          <div className="tm-card flex items-center gap-3 text-[color:var(--tm-ink-3)] mb-6" data-testid="learn-generating">
            <Loader2 size={18} className="animate-spin" /> Making a small lesson just for {student.name}…
          </div>
        )}

        {lesson && (
          <div className="tm-card mb-8" data-testid="learn-lesson">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h2 className="text-2xl font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>
                {lesson.title}
              </h2>
              <SpeakButton text={`${lesson.title}. ${lesson.steps.join(". ")}`} testId="lesson-speak-btn" />
            </div>
            <p className="tm-chip mb-4">{lesson.interest}</p>
            <ol className="space-y-3" data-testid="lesson-steps">
              {lesson.steps.map((s, i) => (
                <li className="tm-numbered-step" key={`${lesson.id}-step-${i}`}>
                  <span className="tm-numbered-step-num">{i + 1}</span>
                  <span className="text-lg leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={complete}
                disabled={lesson.completed}
                className="tm-btn tm-btn-primary"
                data-testid="lesson-complete-btn"
              >
                <CheckCircle2 size={18} />
                {lesson.completed ? "Finished" : "Mark as done"}
              </button>
              <button onClick={() => setLesson(null)} className="tm-btn tm-btn-outline" data-testid="lesson-new-btn">
                Pick something else
              </button>
            </div>
          </div>
        )}

        {pastLessons.length > 0 && (
          <section data-testid="past-lessons">
            <h3 className="text-lg font-bold mb-3" style={{ fontFamily: "Nunito, sans-serif" }}>
              Lessons you made before
            </h3>
            <ul className="space-y-2">
              {pastLessons.slice(0, 10).map((l) => (
                <li key={l.id} className="tm-card-soft flex items-center justify-between gap-3">
                  <div>
                    <p className="font-bold">{l.title}</p>
                    <p className="text-sm text-[color:var(--tm-ink-3)]">{l.interest} · {l.completed ? "done" : "not yet done"}</p>
                  </div>
                  <button onClick={() => setLesson(l)} className="tm-btn tm-btn-ghost" data-testid={`past-lesson-open-${l.id}`}>
                    Open
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
}
